import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ProfileFormData,
  SobrietyDateFormData,
  NotificationPreferencesFormData,
} from '../types';

// Onboarding step identifiers
export type OnboardingStep =
  | 'welcome'
  | 'profile'
  | 'programs'
  | 'sobriety'
  | 'sponsor'
  | 'notifications'
  | 'tour'
  | 'completion';

export const ONBOARDING_STEPS: OnboardingStep[] = [
  'welcome',
  'profile',
  'programs',
  'sobriety',
  'sponsor',
  'notifications',
  'tour',
  'completion',
];

// Step metadata for UI
export const stepMetadata: Record<OnboardingStep, { title: string; subtitle: string }> = {
  welcome: {
    title: 'Welcome',
    subtitle: 'Begin your journey',
  },
  profile: {
    title: 'Your Profile',
    subtitle: 'Tell us about yourself',
  },
  programs: {
    title: 'Recovery Programs',
    subtitle: 'Select your programs',
  },
  sobriety: {
    title: 'Sobriety Dates',
    subtitle: 'Track your progress',
  },
  sponsor: {
    title: 'Sponsor Connection',
    subtitle: 'Connect with support',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Set your reminders',
  },
  tour: {
    title: 'Quick Tour',
    subtitle: 'Learn the features',
  },
  completion: {
    title: 'All Set!',
    subtitle: "You're ready to begin",
  },
};

// Custom program entry
export interface CustomProgram {
  id: string;
  name: string;
  abbreviation: string; // 2-10 characters
  description?: string;
  website?: string; // Optional URL
}

// Collected onboarding data
export interface OnboardingData {
  profile: ProfileFormData | null;
  selectedPrograms: string[];
  customPrograms: CustomProgram[];
  sobrietyDates: SobrietyDateFormData[];
  hasSponsor: boolean | null;
  sponsorInviteCode: string | null;
  notifications: NotificationPreferencesFormData | null;
  tourCompleted: boolean;
}

// Initial empty onboarding data
const initialOnboardingData: OnboardingData = {
  profile: null,
  selectedPrograms: [],
  customPrograms: [],
  sobrietyDates: [],
  hasSponsor: null,
  sponsorInviteCode: null,
  notifications: null,
  tourCompleted: false,
};

// Default notification preferences
export const defaultNotificationPreferences: NotificationPreferencesFormData = {
  checkInReminderEnabled: true,
  checkInReminderTime: '09:00',
  nightlyReviewReminderEnabled: true,
  nightlyReviewReminderTime: '21:00',
  pushMilestones: true,
  pushSponsorMessages: true,
  pushDailyReflection: true,
  pushMeetings: true,
  emailWeeklyDigest: true,
  emailSponsorUpdates: true,
};

// Onboarding store state
interface OnboardingState {
  // Current step
  currentStep: OnboardingStep;
  currentStepIndex: number;

  // Completion status
  hasCompletedOnboarding: boolean;

  // Collected data
  data: OnboardingData;

  // Navigation
  goToStep: (step: OnboardingStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  canGoNext: () => boolean;
  canGoPrevious: () => boolean;

  // Data setters
  setProfile: (profile: ProfileFormData) => void;
  setSelectedPrograms: (programs: string[]) => void;
  addCustomProgram: (program: CustomProgram) => void;
  removeCustomProgram: (id: string) => void;
  addSobrietyDate: (date: SobrietyDateFormData) => void;
  removeSobrietyDate: (index: number) => void;
  updateSobrietyDate: (index: number, date: SobrietyDateFormData) => void;
  setSponsorInfo: (hasSponsor: boolean, inviteCode?: string) => void;
  setNotifications: (notifications: NotificationPreferencesFormData) => void;
  setTourCompleted: (completed: boolean) => void;

  // Completion
  markAsCompleted: () => void;

  // Reset
  reset: () => void;

  // Computed
  getProgress: () => number;
  isStepComplete: (step: OnboardingStep) => boolean;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      currentStep: 'welcome',
      currentStepIndex: 0,
      hasCompletedOnboarding: false,
      data: initialOnboardingData,

      goToStep: (step) => {
        const index = ONBOARDING_STEPS.indexOf(step);
        if (index !== -1) {
          set({ currentStep: step, currentStepIndex: index });
        }
      },

      nextStep: () => {
        const { currentStepIndex } = get();
        if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
          const nextIndex = currentStepIndex + 1;
          set({
            currentStep: ONBOARDING_STEPS[nextIndex],
            currentStepIndex: nextIndex,
          });
        }
      },

      previousStep: () => {
        const { currentStepIndex } = get();
        if (currentStepIndex > 0) {
          const prevIndex = currentStepIndex - 1;
          set({
            currentStep: ONBOARDING_STEPS[prevIndex],
            currentStepIndex: prevIndex,
          });
        }
      },

      canGoNext: () => {
        const { currentStepIndex } = get();
        return currentStepIndex < ONBOARDING_STEPS.length - 1;
      },

      canGoPrevious: () => {
        const { currentStepIndex } = get();
        return currentStepIndex > 0;
      },

      setProfile: (profile) => {
        set((state) => ({
          data: { ...state.data, profile },
        }));
      },

      setSelectedPrograms: (programs) => {
        console.log('🔵 setSelectedPrograms called with:', programs);
        set((state) => {
          console.log('🔵 Current state before update:', {
            selectedPrograms: state.data.selectedPrograms,
            customPrograms: state.data.customPrograms,
          });
          const newState = {
            data: { ...state.data, selectedPrograms: programs },
          };
          console.log('🔵 New state after update:', {
            selectedPrograms: newState.data.selectedPrograms,
          });
          return newState;
        });
        // Log state after set completes
        const finalState = get();
        console.log('🔵 Final persisted state:', {
          selectedPrograms: finalState.data.selectedPrograms,
          customPrograms: finalState.data.customPrograms,
        });
      },

      addCustomProgram: (program) => {
        set((state) => ({
          data: {
            ...state.data,
            customPrograms: [...state.data.customPrograms, program],
            selectedPrograms: [...state.data.selectedPrograms, program.id],
          },
        }));
      },

      removeCustomProgram: (id) => {
        set((state) => ({
          data: {
            ...state.data,
            customPrograms: state.data.customPrograms.filter((p) => p.id !== id),
            selectedPrograms: state.data.selectedPrograms.filter((pid) => pid !== id),
          },
        }));
      },

      addSobrietyDate: (date) => {
        set((state) => ({
          data: {
            ...state.data,
            sobrietyDates: [...state.data.sobrietyDates, date],
          },
        }));
      },

      removeSobrietyDate: (index) => {
        set((state) => ({
          data: {
            ...state.data,
            sobrietyDates: state.data.sobrietyDates.filter((_, i) => i !== index),
          },
        }));
      },

      updateSobrietyDate: (index, date) => {
        set((state) => ({
          data: {
            ...state.data,
            sobrietyDates: state.data.sobrietyDates.map((d, i) => (i === index ? date : d)),
          },
        }));
      },

      setSponsorInfo: (hasSponsor, inviteCode) => {
        set((state) => ({
          data: {
            ...state.data,
            hasSponsor,
            sponsorInviteCode: inviteCode || null,
          },
        }));
      },

      setNotifications: (notifications) => {
        set((state) => ({
          data: { ...state.data, notifications },
        }));
      },

      setTourCompleted: (completed) => {
        set((state) => ({
          data: { ...state.data, tourCompleted: completed },
        }));
      },

      markAsCompleted: () => {
        console.log('✅ Marking onboarding as completed');
        set({ hasCompletedOnboarding: true });
      },

      reset: () => {
        set({
          currentStep: 'welcome',
          currentStepIndex: 0,
          hasCompletedOnboarding: false,
          data: initialOnboardingData,
        });
      },

      getProgress: () => {
        const { currentStepIndex } = get();
        return Math.round(((currentStepIndex + 1) / ONBOARDING_STEPS.length) * 100);
      },

      isStepComplete: (step) => {
        const { data } = get();
        switch (step) {
          case 'welcome':
            return true; // Always complete (just informational)
          case 'profile':
            return data.profile !== null;
          case 'programs':
            return data.selectedPrograms.length > 0;
          case 'sobriety':
            return true; // Optional - can skip
          case 'sponsor':
            return data.hasSponsor !== null;
          case 'notifications':
            return data.notifications !== null;
          case 'tour':
            return data.tourCompleted;
          case 'completion':
            return false; // Never "complete" - it's the final step
          default:
            return false;
        }
      },
    }),
    {
      name: 'reprieve-onboarding',
      // Only persist the data and current step, not the functions
      partialize: (state) => ({
        currentStep: state.currentStep,
        currentStepIndex: state.currentStepIndex,
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        data: state.data,
      }),
      // Log when hydration completes
      onRehydrateStorage: () => (state) => {
        if (state) {
          console.log('🔄 Onboarding store rehydrated:', {
            hasCompletedOnboarding: state.hasCompletedOnboarding,
            selectedPrograms: state.data?.selectedPrograms || [],
            customPrograms: state.data?.customPrograms || [],
            currentStep: state.currentStep,
            profile: state.data?.profile
              ? {
                  firstName: state.data.profile.firstName,
                  lastInitial: state.data.profile.lastInitial,
                }
              : null,
            hasSponsor: state.data?.hasSponsor ?? null,
          });
        }
      },
    }
  )
);

// Hook for easy access to onboarding state
export function useOnboarding() {
  const store = useOnboardingStore();
  return {
    ...store,
    stepInfo: stepMetadata[store.currentStep],
    progress: store.getProgress(),
    totalSteps: ONBOARDING_STEPS.length,
  };
}
